import React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { toast } from 'react-hot-toast';

import { Link } from 'ui/base/link';
import { defaultToast, ToastID } from 'ui/base/toast';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { SwitchInput } from 'ui/standard/input/switch_input/switch_input';

import { ResultKind } from 'services/base/types';
import { BasePath } from 'services/base/page_path';
import { IPangolinDomain, Schedule, ScheduleDays } from 'services/pangolin/types';

import { ORDERED_WEEKDAYS, getMicroWeekday } from 'assets/enums/weekdays/weekdays';

import { ScheduleDisplayStore } from './schedule_display_store';
import baseStyles from './schedule_display.module.css';

type ScheduleDisplayStyles = {
  container: string;
  link: string;
  column: string;
  labelName: string;
  secondRow: string;
  time: string;
  weekdays: string;
  inactiveWeekday: string;
};

const themedStyles: ThemedStyles<ScheduleDisplayStyles> = {
  [Theme.DAYLIGHT]: {
    container: baseStyles.baseContainer,
    link: baseStyles.daylightLink,
    column: baseStyles.baseColumn,
    labelName: baseStyles.daylightLabelName,
    secondRow: baseStyles.baseSecondRow,
    time: baseStyles.daylightTime,
    weekdays: baseStyles.daylightWeekdays,
    inactiveWeekday: baseStyles.daylightInactiveWeekday,
  },
  [Theme.MIDNIGHT]: {
    link: baseStyles.midnightLink,
    container: baseStyles.baseContainer,
    column: baseStyles.baseColumn,
    labelName: baseStyles.midnightLabelName,
    secondRow: baseStyles.baseSecondRow,
    time: baseStyles.midnightTime,
    weekdays: baseStyles.midnightWeekdays,
    inactiveWeekday: baseStyles.midnightInactiveWeekday,
  },
};

const DaysDisplay = observer(({ days }: { days: ScheduleDays }) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.weekdays}>
      {ORDERED_WEEKDAYS.map((day) => (
        <span key={day} className={classNames({ [styles.inactiveWeekday]: !days[day] })}>
          {getMicroWeekday(day)}
        </span>
      ))}
    </div>
  );
});

type ScheduleDisplayProps = {
  schedule: Schedule;
  domain: IPangolinDomain;
};

export const ScheduleDisplay = ({ schedule, domain }: ScheduleDisplayProps) => {
  const styles = useStyles(themedStyles);

  const time = `${schedule.start.toHumanString()} - ${schedule.end.toHumanString()}`;

  const [store, setStore] = React.useState(() => new ScheduleDisplayStore(schedule));
  React.useEffect(() => { setStore(new ScheduleDisplayStore(schedule)); }, [schedule]);

  const handleToggle = React.useCallback(async() => {
    const result = await store.handleToggle(domain);
    if (result.kind === ResultKind.OK) {
      toast.dismiss(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      toast.dismiss(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      toast.dismiss(ToastID.ERROR_ACTION_GENERIC);
      return true;
    } else if (result.kind === ResultKind.USER_ERROR && result.status === 402) {
      defaultToast(ToastID.ERROR_ACTION_PLAN_REQUIRED);
      return false;
    } else if (result.kind === ResultKind.SYSTEM_ERROR && result.status === 502) {
      defaultToast(ToastID.ERROR_ACTION_PANGOLIN_OFFLINE);
      return false;
    } else {
      defaultToast(ToastID.ERROR_ACTION_GENERIC);
      return false;
    }
  }, [store, domain]);

  return (
    <div className={styles.container}>
      <Link path={{
        kind: BasePath.SCHEDULE_VIEW,
        pangolinID: domain.id,
        scheduleID: schedule.uuid,
      }} className={styles.link}>
        <div className={styles.column}>
          <div className={styles.labelName}>
            {schedule.name}
          </div>
          <div className={styles.secondRow}>
            <div className={styles.time}>
              {time}
            </div>
            <DaysDisplay days={schedule.days}/>
          </div>
        </div>
      </Link>
      <SwitchInput store={store.enabled} onChange={handleToggle}/>
    </div>
  );
};

